//
// # ConstraintsPanel
//
// Panel showing symmetry constraints for the active design.
// Groups are shown as collapsible sections; standalone matches appear
// in a separate section at the bottom.
//
// Click a match row to select those instances on the schematic canvas.
// Hover a match row for a transient highlight preview.
//
// For schematics, provides a button to infer constraints from topology.
//

import React, { useState, useCallback } from "react";
import { GitBranch, Layers, Play, Plus, Wand2 } from "lucide-react";
import { ConstraintGroupSection } from "./ConstraintGroupSection";
import { ConstraintMatchRow } from "./ConstraintMatchRow";
import { useConstraints } from "./useConstraints";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import "./constraintsPanel.css";

export function ConstraintsPanel() {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const { constraints, loading, viewKind } = useConstraints();
  const [inferring, setInferring] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [routing, setRouting] = useState(false);

  const handlePlaceAll = async () => {
    setPlacing(true);
    try {
      await workspace.placeAllConstraints();
    } finally {
      setPlacing(false);
    }
  };

  const handleRouteConstraints = async () => {
    setRouting(true);
    try {
      await workspace.routeConstraints();
    } finally {
      setRouting(false);
    }
  };

  const handleInferConstraints = async () => {
    setInferring(true);
    try {
      await workspace.inferConstraints();
    } finally {
      setInferring(false);
    }
  };

  // Check if constraint editing should be enabled.
  // - For schematics: always enabled
  // - For layouts: enabled if there's a linked schematic (has schematicImpl)
  const editor = workspace.getActiveEditor();
  const isSchematicActive =
    snapshot.activeIdent && editor?.content.contentKind === "schematic";
  // Check if layout has a linked schematic by looking at schematicImpl directly
  const layoutContent = editor?.content as
    | { schematicImpl?: unknown }
    | undefined;
  const isLayoutWithSchematic =
    viewKind === "layout" && layoutContent?.schematicImpl !== undefined;
  const canEditConstraints = isSchematicActive || isLayoutWithSchematic;
  const isRunning = inferring || loading || placing || routing;

  // Click on empty space in the panel clears constraint selection
  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      // Only clear if clicking directly on the content area, not a child
      if (e.target === e.currentTarget) {
        workspace.clearConstraintSelection();
      }
    },
    [workspace]
  );

  // Filter to non-empty groups and count total matches
  const nonEmptyGroups =
    constraints?.groups.filter((g) => g.matches && g.matches.length > 0) ?? [];
  const totalMatches = constraints
    ? nonEmptyGroups.reduce((sum, g) => sum + (g.matches?.length ?? 0), 0) +
      constraints.matches.length
    : 0;

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Panel header */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        <div className="flex items-center gap-1.5">
          <Layers size={11} />
          Constraints
        </div>
      </div>

      {/* Infer and Add buttons (schematic or layout with linked schematic) */}
      {canEditConstraints && (
        <div className="p-2 border-b border-[var(--color-ide-border)] flex gap-2">
          <button
            className={
              "flex-1 px-3 py-1.5 rounded flex items-center justify-center gap-1.5 text-[11px] " +
              "bg-[var(--color-ide-surface-hover)] hover:bg-[var(--color-ide-surface-selected)] " +
              "text-[var(--color-ide-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            }
            onClick={handleInferConstraints}
            disabled={isRunning}
            title="Analyze schematic topology to infer symmetry constraints"
          >
            <Wand2 size={12} />
            {inferring ? "Inferring..." : "Infer Constraints"}
          </button>
          <button
            className={
              "flex-1 px-3 py-1.5 rounded flex items-center justify-center gap-1.5 text-[11px] " +
              "bg-[var(--color-ide-surface-hover)] hover:bg-[var(--color-ide-surface-selected)] " +
              "text-[var(--color-ide-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            }
            onClick={() => workspace.openCreateConstraintDialog()}
            disabled={isRunning}
            title="Manually add a new constraint"
          >
            <Plus size={12} />
            Add Constraint
          </button>
          {/* Place All button - only for layouts */}
          {isLayoutWithSchematic && (
            <button
              className={
                "flex-1 px-3 py-1.5 rounded flex items-center justify-center gap-1.5 text-[11px] " +
                "bg-[var(--color-ide-surface-hover)] hover:bg-[var(--color-ide-surface-selected)] " +
                "text-[var(--color-ide-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              }
              onClick={() => void handlePlaceAll()}
              disabled={isRunning || totalMatches === 0}
              title="Place all constraints symmetrically"
            >
              <Play size={12} />
              {placing ? "Placing..." : "Place All"}
            </button>
          )}
          {/* Route Constraints button - only for layouts */}
          {isLayoutWithSchematic && (
            <button
              className={
                "flex-1 px-3 py-1.5 rounded flex items-center justify-center gap-1.5 text-[11px] " +
                "bg-[var(--color-ide-surface-hover)] hover:bg-[var(--color-ide-surface-selected)] " +
                "text-[var(--color-ide-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              }
              onClick={() => void handleRouteConstraints()}
              disabled={isRunning || totalMatches === 0}
              title="Route nets symmetrically based on constraints"
            >
              <GitBranch size={12} />
              {routing ? "Routing..." : "Route Constraints"}
            </button>
          )}
        </div>
      )}

      {/* Content area - click empty space to clear constraint selection */}
      <div
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
        onClick={handleBackgroundClick}
      >
        {loading && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-muted)]">
            Loading...
          </div>
        )}

        {!loading && !constraints && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            {viewKind
              ? "No constraints defined."
              : "Open a schematic or layout to view constraints."}
          </div>
        )}

        {!loading && constraints && totalMatches === 0 && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            No constraints defined.
          </div>
        )}

        {!loading && constraints && totalMatches > 0 && (
          <div className="p-2">
            {/* Summary line */}
            <div className="px-2 pb-2 text-[10px] text-[var(--color-ide-text-muted)]">
              {nonEmptyGroups.length} group
              {nonEmptyGroups.length !== 1 ? "s" : ""}, {totalMatches} match
              {totalMatches !== 1 ? "es" : ""}
              {viewKind === "layout" && (
                <span className="ml-1 opacity-60">(from schematic)</span>
              )}
            </div>

            {/* Groups - only show non-empty groups */}
            {nonEmptyGroups.map((group) => (
              <ConstraintGroupSection key={group.id} group={group} />
            ))}

            {/* Standalone matches */}
            {constraints.matches.length > 0 && (
              <div className="mt-2">
                <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-dim)]">
                  Standalone
                </div>
                <div className="flex flex-col gap-1 px-2 pt-1">
                  {constraints.matches.map((match) => (
                    <ConstraintMatchRow key={match.id} match={match} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
